import { EyeIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { Button } from '@/components/Button';
import { Container } from '@/components/ui';
import Integrations from '@/sections/Integrations';
import { cn } from '@/utils/classname';

import { IntegrationMonitoringSectionProps } from './types';

/**
 * IntegrationMonitoringSection - Complete integration monitoring section component
 *
 * Wraps the existing Integrations component in a bordered section with:
 * - Section header with title and optional "View" button
 * - Integration list showing connected integrations and their status
 * - Loading states
 *
 * @example
 * // Basic usage with navigation
 * <IntegrationMonitoringSection
 *   customerEmail="user@example.com"
 *   onViewAll={() => navigate('/integrations')}
 * />
 *
 * @example
 * // Read-only mode (no action button)
 * <IntegrationMonitoringSection
 *   customerEmail="user@example.com"
 *   showAction={false}
 * />
 */
export const IntegrationMonitoringSection: React.FC<
  IntegrationMonitoringSectionProps
> = ({
  customerEmail,
  onViewAll,
  title = 'Integration Monitoring',
  actionLabel = 'View Integrations',
  showAction = true,
  className,
}) => {
  return (
    <Container variant="emphasized" className={cn(className)}>
      {/* Section Header */}
      <div className="mb-4 flex items-center">
        <h3 className="text-xl font-bold">{title}</h3>
        {showAction && onViewAll && (
          <Button
            className="ml-auto"
            size="xs"
            styleType="rounded"
            startIcon={<EyeIcon className="size-5" />}
            label={actionLabel}
            onClick={onViewAll}
          />
        )}
      </div>

      {/* Integrations List */}
      <Integrations customerEmail={customerEmail} />
    </Container>
  );
};
